/**
 * Skill-gap scoring helpers.
 *
 * The panel compares two 0-10 numbers per tool/skill - the candidate's score and the level
 * the job requires - but only the candidate score is displayed on the cards, as "8.5/10". Nothing is guessed: the job requirement is a real assessment or
 * null (it used to default to 8), and a tool/skill the analysis did not detect in the
 * candidate's projects, resume or quiz counts as 0 -- the product rule -- while the
 * number of skills that actually had evidence is still reported separately.
 */

export const MAX_SKILL_SCORE = 10;
export const MAX_FITMENT_SCORE = 100;

/** Source types that count as demonstrated evidence; anything else scores 0. */
const DETECTED_EVIDENCE_SOURCES = ['project', 'resume', 'both'];

/**
 * Parse a 0-10 score (or a 0-100 one, via `maxValue`).
 *
 * @returns {number|null} the score clamped to the scale, or null when the value is
 *   missing, blank, non-numeric or not finite. null means "not assessed" and is kept
 *   distinct from a genuine 0.
 */
export function parseScore(value, maxValue = MAX_SKILL_SCORE) {
  if (value === null || value === undefined || typeof value === 'boolean') return null;

  let numeric;
  if (typeof value === 'number') {
    numeric = value;
  } else if (typeof value === 'string') {
    // "6.5 / 10" and "85%" carry a leading number; labels like "N/A" do not.
    const match = value.trim().match(/^[+-]?(\d+(\.\d*)?|\.\d+)/);
    if (!match) return null;
    numeric = Number(match[0]);
  } else {
    return null;
  }

  if (!Number.isFinite(numeric)) return null;
  const clamped = Math.min(maxValue, Math.max(0, numeric));
  return Math.round(clamped * 10) / 10;
}

/** Fitment stays a 0-100 percentage, or null when the analysis has no usable value. */
export function parseFitmentScore(value) {
  return parseScore(value, MAX_FITMENT_SCORE);
}

/**
 * Safely parse job skills (JSON array string, array of strings/objects, or comma-separated string)
 * into a structured list preserving both skill name and required score when present.
 *
 * @param {any} val
 * @returns {Array<string|{skill?: string, name?: string, required_score?: number}>}
 */
export function parseRawJobSkills(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return [];
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') return [parsed];
      } catch (_) {}
    }
    return trimmed.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Label for a card's candidate score out of 10, e.g. "Your Skills: 9.5/10".
 *
 * The job requirement is still carried on `B` for the comparison and the chart, but it
 * is not shown on the card any more. The score is clamped to the 0-10 scale so a stale
 * or hand-edited value can never render as e.g. "12/10" or "-1/10".
 *
 * @param {{A?: number|null}} item a mapped skill-gap row
 * @returns {string}
 */
export function formatScorePair(item) {
  const rawCandidate = item && Number.isFinite(item.A) ? item.A : 0;
  const candidate = Math.min(MAX_SKILL_SCORE, Math.max(0, rawCandidate));
  return `Your Skills: ${candidate}/10`;
}

/**
 * Map raw analysis rows (the `tools` / `skills` arrays from /ai-agents/classify-skills)
 * into the objects the panel renders.
 *
 * A skill counts as demonstrated only when the analysis found it in a project, the
 * resume, or both. Everything else - self-declared ("profile"), "missing", or no score
 * at all - is reported as 0, because the model happily emits a number next to its own
 * "None - Not detected in projects" provenance. `hasEvidence` records whether the 0 came
 * from an absence of evidence rather than a measured score.
 *
 * @param {Array} skillsArrayRaw
 * @param {{knowledgeScores?: Array, studentSkills?: Array}} options
 * @returns {Array<{subject: string, A: number, B: number|null, hasEvidence: boolean,
 *   isComparable: boolean, source_type: string|null, project_name: string,
 *   project_url: string|null, explanation: string}>}
 */
export function mapSkillGapItems(skillsArrayRaw, options = {}) {
  const knowledgeScores = Array.isArray(options.knowledgeScores) ? options.knowledgeScores : [];
  const rows = Array.isArray(skillsArrayRaw) ? skillsArrayRaw : [];

  return rows.map((skillObj) => {
    const isObject = typeof skillObj === 'object' && skillObj !== null;
    const skillName = isObject ? (skillObj.name || 'Unknown') : String(skillObj);
    const lowerSkill = skillName.toLowerCase();

    const requiredScore = parseScore(isObject ? skillObj.required_score : undefined);
    const modelScore = parseScore(isObject ? skillObj.student_score : undefined);

    const rawSourceType = isObject && typeof skillObj.source_type === 'string'
      ? skillObj.source_type.trim().toLowerCase()
      : null;
    const rawProjectName = isObject ? skillObj.project_name : null;
    const rawExplanation = isObject ? skillObj.explanation : null;

    // Only project/resume evidence counts as "detected". The model marks self-declared
    // skills as "profile" yet still scores them, and its own provenance text usually
    // reads "None - Not detected in projects" - that is what showed a not-detected
    // skill as a 6 next to a requirement of 7.
    const provenanceText = typeof rawProjectName === 'string' ? rawProjectName.trim().toLowerCase() : '';
    const provenanceSaysMissing = provenanceText.startsWith('none') || provenanceText.includes('not detected');
    const detected = rawSourceType
      ? DETECTED_EVIDENCE_SOURCES.includes(rawSourceType) && !provenanceSaysMissing
      : !provenanceSaysMissing;

    let studentScore = null;
    if (detected) {
      studentScore = modelScore;
      if (studentScore === null) {
        // Existing second source: the per-skill knowledge score from the assessment quiz.
        const knowledgeEntry = knowledgeScores.find(
          (k) => k && typeof k.name === 'string' && (
            k.name.toLowerCase().includes(lowerSkill) || lowerSkill.includes(k.name.toLowerCase())
          )
        );
        studentScore = knowledgeEntry ? parseScore(knowledgeEntry.score) : null;
      }
    }

    // Product rule: anything not demonstrated counts as 0 for the candidate. A 0 still
    // has to be compared against a real job requirement - hence `isComparable` only
    // checks the requirement.
    const hasEvidence = detected && studentScore !== null;
    const candidateScore = hasEvidence ? studentScore : 0;
    const isComparable = requiredScore !== null;
    const meetsRequirement = isComparable && candidateScore >= requiredScore - 0.5;

    let sourceType = rawSourceType;
    let projectName = rawProjectName;
    let explanation = rawExplanation;
    const weakExplanation = typeof explanation === 'string' && explanation.startsWith('This skill was not assessed');

    if (!isComparable) {
      // The analysis produced no requirement level, so there is nothing to compare to.
      if (!sourceType) sourceType = 'unknown';
      if (!projectName || projectName === 'string') projectName = 'No requirement level in this analysis';
      if (!explanation || explanation === 'string') {
        explanation = `The analysis produced no requirement level for ${skillName}, so it is not part of the comparison.`;
      }
    } else if (!hasEvidence) {
      // Required by the role, but nothing demonstrated in projects or resume.
      sourceType = 'missing';
      projectName = 'Not detected in projects';
      if (!explanation || explanation === 'string' || weakExplanation) {
        explanation = `No project or resume evidence was detected for ${skillName}, so it is counted as 0. Building a hands-on project with ${skillName} will help bridge this gap.`;
      }
    } else if (meetsRequirement) {
      if (!sourceType) sourceType = 'project';
      if (!projectName || projectName === 'string') projectName = 'Verified in Technical Portfolio';
      if (!explanation || explanation === 'string') {
        explanation = `Evidence for ${skillName} was verified from your projects and technical background.`;
      }
    } else {
      if (!sourceType) sourceType = 'missing';
      if (!projectName || projectName === 'string') projectName = 'Not detected in projects';
      if (!explanation || explanation === 'string') {
        explanation = `No project or resume evidence found yet for ${skillName}. Building a hands-on project with ${skillName} will help bridge this gap.`;
      }
    }

    return {
      subject: skillName,
      A: candidateScore,
      B: requiredScore,
      hasEvidence,
      isComparable,
      source_type: sourceType,
      project_name: projectName,
      project_url: isObject ? (skillObj.project_url || null) : null,
      explanation,
      assessment_status: isObject ? (skillObj.assessment_status || (hasEvidence ? 'ASSESSED' : 'NO_EVIDENCE')) : 'UNASSESSED',
      rubric_breakdown: isObject && skillObj.rubric_breakdown ? skillObj.rubric_breakdown : null,
      importance: isObject && skillObj.importance ? skillObj.importance : null,
    };
  });
}
